import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import getChartColorsArray from "../../Components/Common/ChartsDynamicColor";
import { APIClient } from "../../helpers/api_helper";
import { Spinner } from "reactstrap";

const apiClient = new APIClient();

const formatPeriodLabel = (period) => {
  if (!period) return "";
  if (period.length === 7 && period[4] === "-") {
    const [y, m] = period.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  }
  return period;
};

const EarningsVsSpendChart = () => {
  const [chartData, setChartData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState("month");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { groupBy };
      const response = await apiClient.get("/dashboard/earnings-vs-spend", params);
      const raw = response?.data ?? response;
      const data = Array.isArray(raw) ? raw : [];
      const periods = data.map((d) => d.period);
      const earnings = data.map((d) => Number(d.earnings) || 0);
      const spend = data.map((d) => Number(d.spend) || 0);
      setCategories(periods.map(formatPeriodLabel));
      setChartData([
        { name: "Earnings", data: earnings },
        { name: "Spend", data: spend },
      ]);
    } catch (err) {
      setError(err?.message || "Failed to load chart data");
      setChartData([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [groupBy]);

  const chartColors = getChartColorsArray('["--vz-success", "--vz-danger"]');
  const options = {
    chart: {
      height: 350,
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      curve: "smooth",
      width: [2, 2],
    },
    xaxis: {
      categories,
      axisTicks: { show: false },
      axisBorder: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val) => (val != null ? "₹" + Number(val).toLocaleString() : ""),
      },
    },
    grid: {
      show: true,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { top: 0, right: -2, bottom: 15, left: 10 },
    },
    legend: {
      show: true,
      horizontalAlign: "center",
      offsetY: -5,
    },
    colors: chartColors,
    tooltip: {
      shared: true,
      y: [
        { formatter: (y) => (y != null ? "₹" + Number(y).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "") },
        { formatter: (y) => (y != null ? "₹" + Number(y).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "") },
      ],
    },
  };

  const totalEarnings = chartData[0]?.data?.reduce((a, b) => a + b, 0) ?? 0;
  const totalSpend = chartData[1]?.data?.reduce((a, b) => a + b, 0) ?? 0;

  return (
    <Card>
      <CardHeader className="border-0 align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1">Earnings vs Spend</h4>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm ${groupBy === "month" ? "btn-primary" : "btn-soft-secondary"}`}
            onClick={() => setGroupBy("month")}
          >
            By Month
          </button>
          <button
            type="button"
            className={`btn btn-sm ${groupBy === "week" ? "btn-primary" : "btn-soft-secondary"}`}
            onClick={() => setGroupBy("week")}
          >
            By Week
          </button>
        </div>
      </CardHeader>
      {!loading && !error && chartData.length > 0 && (
        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1 text-success">
                  <CountUp start={0} end={totalEarnings} duration={2} separator="," prefix="₹" decimals={2} />
                </h5>
                <p className="text-muted mb-0">Total Earnings</p>
              </div>
            </Col>
            <Col xs={6}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1 text-danger">
                  <CountUp start={0} end={totalSpend} duration={2} separator="," prefix="₹" decimals={2} />
                </h5>
                <p className="text-muted mb-0">Total Spend</p>
              </div>
            </Col>
          </Row>
        </CardHeader>
      )}
      <CardBody className="p-0 pb-2">
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-danger">{error}</div>
        ) : chartData.length === 0 || categories.length === 0 ? (
          <div className="p-4 text-center text-muted">No data for the selected period.</div>
        ) : (
          <div className="w-100" dir="ltr">
            <ReactApexChart
              options={options}
              series={chartData}
              type="line"
              height={350}
              className="apex-charts"
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default EarningsVsSpendChart;
